import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import WahaSessionsAPI from '../../api/wahaSessions';
import { throwErrorMessage } from '../utils/api';

export const state = {
  records: [],
  uiFlags: {
    isFetching: false,
    isFetchingItem: false,
    isCreating: false,
    isDeleting: false,
    isUpdating: false,
    isStarting: false,
    isStopping: false,
    isRestarting: false,
    isLoggingOut: false,
    isSyncing: false,
  },
};

export const getters = {
  getSessions($state) {
    return $state.records;
  },
  getUIFlags($state) {
    return $state.uiFlags;
  },
  getSession: $state => sessionId => {
    const [session] = $state.records.filter(
      record => record.id === Number(sessionId)
    );
    return session || {};
  },
};

export const actions = {
  get: async ({ commit }) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isFetching: true });
    try {
      const response = await WahaSessionsAPI.get();
      commit(types.SET_WAHA_SESSIONS, response.data.payload);
    } catch (error) {
      throwErrorMessage(error);
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isFetching: false });
    }
  },

  create: async ({ commit }, sessionData) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isCreating: true });
    try {
      const response = await WahaSessionsAPI.create(sessionData);
      commit(types.ADD_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isCreating: false });
    }
  },

  update: async ({ commit }, { id, ...updateData }) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isUpdating: true });
    try {
      const response = await WahaSessionsAPI.update(id, updateData);
      commit(types.EDIT_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isUpdating: false });
    }
  },

  delete: async ({ commit }, sessionId) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isDeleting: true });
    try {
      await WahaSessionsAPI.delete(sessionId);
      commit(types.DELETE_WAHA_SESSION, sessionId);
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isDeleting: false });
    }
  },

  start: async ({ commit }, sessionId) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isStarting: true });
    try {
      const response = await WahaSessionsAPI.start(sessionId);
      commit(types.EDIT_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isStarting: false });
    }
  },

  stop: async ({ commit }, sessionId) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isStopping: true });
    try {
      const response = await WahaSessionsAPI.stop(sessionId);
      commit(types.EDIT_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isStopping: false });
    }
  },

  restart: async ({ commit }, sessionId) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isRestarting: true });
    try {
      const response = await WahaSessionsAPI.restart(sessionId);
      commit(types.EDIT_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isRestarting: false });
    }
  },

  logout: async ({ commit }, sessionId) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isLoggingOut: true });
    try {
      const response = await WahaSessionsAPI.logout(sessionId);
      commit(types.EDIT_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isLoggingOut: false });
    }
  },

  getQrCode: async (context, { id, format }) => {
    try {
      const response = await WahaSessionsAPI.getQrCode(id, format);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    }
  },

  getStatus: async ({ commit }, sessionId) => {
    try {
      const response = await WahaSessionsAPI.getStatus(sessionId);
      commit(types.EDIT_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    }
  },

  updateConfig: async ({ commit }, { id, config }) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isUpdating: true });
    try {
      const response = await WahaSessionsAPI.updateConfig(id, config);
      commit(types.EDIT_WAHA_SESSION, response.data);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isUpdating: false });
    }
  },

  sync: async ({ commit }) => {
    commit(types.SET_WAHA_SESSION_UI_FLAG, { isSyncing: true });
    try {
      const response = await WahaSessionsAPI.sync();
      commit(types.SET_WAHA_SESSIONS, response.data.payload);
      return response.data;
    } catch (error) {
      throwErrorMessage(error);
      throw error;
    } finally {
      commit(types.SET_WAHA_SESSION_UI_FLAG, { isSyncing: false });
    }
  },
};

export const mutations = {
  [types.SET_WAHA_SESSION_UI_FLAG]($state, data) {
    $state.uiFlags = {
      ...$state.uiFlags,
      ...data,
    };
  },

  [types.SET_WAHA_SESSIONS]: MutationHelpers.set,
  [types.ADD_WAHA_SESSION]: MutationHelpers.create,
  [types.EDIT_WAHA_SESSION]: MutationHelpers.update,
  [types.DELETE_WAHA_SESSION]: MutationHelpers.destroy,
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
